import { Sunrise } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { BusinessDay } from '@/types'

interface StartTheDayBannerProps {
  /** Today's business day, or null when nobody has started one yet. */
  currentDay: BusinessDay | null
  /**
   * True while the day-status query is still in flight.
   *
   * Required, not optional: `currentDay` is null both when no day exists AND
   * before the first response lands, so without this the banner claims "Today
   * hasn't been started" for a moment on EVERY counter load — including days
   * that are open. A false alarm shown that often stops being read at all.
   */
  isLoading: boolean
  onStartClick: () => void
}

/**
 * Shown at the top of the counter when today has no business day yet.
 *
 * F-CTRL-N32 removed auto-open: the first sale is blocked (409 `day_not_open`)
 * until a human states what is in the drawer. Without this banner the till
 * operator would only discover that halfway through ringing up a customer, so
 * we surface it the moment the counter loads and let them start the day in
 * place — counter, manager and admin may all do it.
 *
 * Hidden as soon as a day exists in any state; from then on DailyClosingBanner
 * takes over the nudging.
 */
export function StartTheDayBanner({
  currentDay,
  isLoading,
  onStartClick,
}: StartTheDayBannerProps) {
  if (isLoading) return null
  if (currentDay) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm font-medium text-amber-900 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-100">
      <div className="flex min-w-0 items-center gap-2">
        <Sunrise className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <span className="min-w-0">
          Today hasn't been started — count the drawer and start the day before the first
          sale.
        </span>
      </div>
      <Button size="sm" className="h-9 shrink-0" onClick={onStartClick}>
        Start the day
      </Button>
    </div>
  )
}
